import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import type { U2000AutoCompleteSearchResult } from "../u2000Core/types/U2000AutoCompleteSearchResult";
import { u2000ExtractAutoCompleteResults } from "../u2000Core/utils/u2000ExtractAutoCompleteResults";
import { doFindCountries, doFindStates, luHandleAppiError as u3120LuHandleAppiError } from "../u3120Customer/U3120Service";

const U3120_NS = "u3120";

export type U3110FindSearchCriterias = { fieldName: string; value: unknown }[];

/** Handler hook pour la recherche Pays (findCountries, U3120). Retourne search(criterias) => Promise<results>. */
export function useU3110FindCountriesHandlers() {
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3110FindSearchCriterias): Promise<U2000AutoCompleteSearchResult<Record<string, unknown>>> => {
      try {
        const response = await doFindCountries(u2000AxiosAuthenticated, criterias);
        return u2000ExtractAutoCompleteResults<Record<string, unknown>>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, U3120_NS, u3120LuHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [u2000AxiosAuthenticated, t, toast],
  );

  return { search };
}

/**
 * Handler hook pour la recherche Province/État (findStates, U3120).
 *
 * Si un identifiant de pays valide est fourni via `countryIdProvider`, le hook l'injecte
 * automatiquement dans les critères (`{ fieldName: "countryId", value }`) avant l'appel API.
 */
export function useU3110FindStatesHandlers(countryIdProvider?: () => string | null | undefined) {
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3110FindSearchCriterias): Promise<U2000AutoCompleteSearchResult<Record<string, unknown>>> => {
      try {
        const enriched: U3110FindSearchCriterias = [...criterias];
        const cid = countryIdProvider?.();
        if (cid != null && String(cid).trim() !== "") {
          enriched.push({ fieldName: "countryId", value: String(cid) });
        }
        const response = await doFindStates(u2000AxiosAuthenticated, enriched);
        return u2000ExtractAutoCompleteResults<Record<string, unknown>>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, U3120_NS, u3120LuHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [u2000AxiosAuthenticated, t, toast, countryIdProvider],
  );

  return { search };
}
